import React, { useEffect, useState } from 'react';
import { Create as CreateIcon, Close as CloseIcon } from '@mui/icons-material';
import { TableCell, TableRow } from '@mui/material';
import * as S from './styles';
import { useDamageCategoryReducer } from '@/main/store/damageCategory';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { getTranslateByKey } from '@/main/services/utils';
import { damageCategoryApi, damageSubCategoryApi } from '@/main/api';
import { toast } from 'react-toastify';
import { KeyValue } from '@/main/models/common';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import DamageSubCategorySidebar from '../damageSubCategorySideBar/damageSubCategorySideBar';
import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';
import ModalConfirm from '@/main/components/shared/modalConfirm/modalConfirm';
import { RowHeaderTextEnd } from '@/main/components/shared/styles';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';

interface Props {
  guidInsurer: string | null;
  handleEdit: (damageCategory: DamageCategoryResponse) => void;
  refreshData: () => void;
}
function DamageCategorySection({
  guidInsurer,
  handleEdit,
  refreshData
}: Props) {
  const {
    damageCategories: damageCategoryReducer
  } = useDamageCategoryReducer();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModalDamageSubCategory, setOpenModalDamageSubCategory] = useState<
    boolean
  >(false);
  const [
    openModalConfirmDeleteCategory,
    setOpenModalConfirmDeleteCategory
  ] = useState<boolean>(false);
  const [
    openModalConfirmDeleteSubCategory,
    setOpenModalConfirmDeleteSubCategory
  ] = useState<boolean>(false);
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  const [
    damageSubCategoryEdit,
    setDamageSubCategoryEdit
  ] = useState<DamageSubCategoryResponse | null>(null);

  const [damageCategories, setDamageCategories] = useState<
    DamageCategoryResponse[]
  >([]);

  const [
    damageCategory,
    setDamageCategory
  ] = useState<DamageCategoryResponse | null>(null);
  const [guidDamageCategoryDelete, setGuidDamageCategoryDelete] = useState<
    string
  >('');
  const [
    guidDamageSubCategoryDelete,
    setGuidDamageSubCategoryDelete
  ] = useState<string>('');

  const rowHeadersSubCategory = [
    getTranslateByKey('information.name'),
    getTranslateByKey('information.partRange'),
    getTranslateByKey('information.repairDays'),
    <RowHeaderTextEnd>{getTranslateByKey('information.actions')}</RowHeaderTextEnd>
  ];

  useEffect(() => {
    if (damageCategoryReducer.data) {
      setDamageCategories(damageCategoryReducer.data);
    }
  }, [damageCategoryReducer.data]);

  async function handleDeleteDamageCategory(guidDamageCategory: string) {
    const resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) return;

    let filesRequest = undefined;
    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    const payload = {
      guidDamageCategory: guidDamageCategory,
      changeJustification: {
        guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
        reasonDescription: resultConfirm.observation || null,
        filesUpload: filesRequest
      }
    };

    setIsLoading(true);
    damageCategoryApi
      .inactiveDamageCategory(payload)
      .then(() => {
        refreshData();
        toast.success(
          getTranslateByKey('information.categoryDeletedSuccessfully')
        );
      })
      .catch((error: any) => {
        error.errorMessages.forEach((e: KeyValue) => {
          toast.error(e.value);
        });
      })
      .finally(() => setIsLoading(false));
  }

  async function handleDeleteDamageSubCategory(guidDamageSubCategory: string) {
    const resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) return;

    let filesRequest = undefined;
    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    const payload = {
      guidDamageSubCategory: guidDamageSubCategory,
      changeJustification: {
        guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
        reasonDescription: resultConfirm.observation || null,
        filesUpload: filesRequest
      }
    };

    setIsLoading(true);
    damageSubCategoryApi
      .inactiveDamageSubCategory(payload)
      .then(() => {
        refreshData();
        toast.success(
          getTranslateByKey('information.subCategoryDeletedSuccessfully')
        );
      })
      .catch((error: any) => {
        error.errorMessages.forEach((e: KeyValue) => {
          toast.error(e.value);
        });
      })
      .finally(() => setIsLoading(false));
  }

  function handleNewDamageSubCategory(damageCategory: DamageCategoryResponse) {
    setDamageSubCategoryEdit(null);
    setDamageCategory(damageCategory);
    setOpenModalDamageSubCategory(true);
  }

  function handleEditDamageSubCategory(
    damageCategory: DamageCategoryResponse,
    damageSubCategory: DamageSubCategoryResponse
  ) {
    setDamageCategory(damageCategory);
    setDamageSubCategoryEdit(damageSubCategory);
    setOpenModalDamageSubCategory(true);
  }

  function closeModalDamageSubCategory() {
    setOpenModalDamageSubCategory(false);
  }

  function handleOpenModalCategoryDelete(guidDamageCategory: string) {
    setGuidDamageCategoryDelete(guidDamageCategory);
    setOpenModalConfirmDeleteCategory(true);
  }

  function handleCloseModalCategoryDelete() {
    setOpenModalConfirmDeleteCategory(false);
  }

  function handleConfirmModalCategoryDelete() {
    setOpenModalConfirmDeleteCategory(false);
    handleDeleteDamageCategory(guidDamageCategoryDelete);
  }

  function handleOpenModalSubCategoryDelete(guidDamageSubCategory: string) {
    setGuidDamageSubCategoryDelete(guidDamageSubCategory);
    setOpenModalConfirmDeleteSubCategory(true);
  }

  function handleCloseModalSubCategoryDelete() {
    setOpenModalConfirmDeleteSubCategory(false);
  }

  function handleConfirmModalSubCategoryDelete() {
    setOpenModalConfirmDeleteSubCategory(false);
    handleDeleteDamageSubCategory(guidDamageSubCategoryDelete);
  }

  return (
    <>
      {damageCategories.map((damageCategory, index) => (
        <div key={`id-damageCategory-item${index}`}>
          <S.ContainerCategory>
            <S.BoxCategoryName>
              {damageCategory.damageCategoryName}
              <SecondaryButton
                id="id-damageSubCategory-add"
                key={`id-damageSubCategory-add${index}`}
                style={{ padding: '.2rem .8rem', margin: '0 0 0 2rem' }}
                onClick={() => handleNewDamageSubCategory(damageCategory)}>
                {getTranslateByKey('information.addSubCategory')}
              </SecondaryButton>
            </S.BoxCategoryName>

            <S.BoxCategoryActions>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <S.TextValue cursor="pointer">
                  <CreateIcon
                    id="id-damageCategory-edit"
                    key={`id-damageCategory-edit${index}`}
                    onClick={() => handleEdit(damageCategory)}
                  />
                </S.TextValue>
                <S.TextValue cursor="pointer">
                  {isLoading ? (
                    <LoadingComponent
                      key={`id-damageCategory-loading${index}`}
                      size={10}
                    />
                  ) : (
                    <CloseIcon
                      id="id-damageCategory-delete"
                      key={`id-damageCategory-delete${index}`}
                      onClick={() =>
                        handleOpenModalCategoryDelete(
                          damageCategory.guidDamageCategory
                        )
                      }
                    />
                  )}
                </S.TextValue>
              </div>
            </S.BoxCategoryActions>
          </S.ContainerCategory>
          {damageCategory.damageSubCategoryResponse &&
            damageCategory.damageSubCategoryResponse.length > 0 && (
              <S.ContainerGrid>
                <BoxGrid
                  rowHeaders={rowHeadersSubCategory}
                  sizeColumns={['30%', '30%', '30%', '80px']}
                >
                  {damageCategory.damageSubCategoryResponse.map(
                    (damageSubCategory, indexSubCategory) => (
                      <TableRow key={`id-row-SubCategory${indexSubCategory}`}>
                        <TableCell>
                          <S.CollumnName>
                            {damageSubCategory.damageSubCategoryName}
                          </S.CollumnName>
                        </TableCell>
                        <TableCell>
                          <S.ContainerLabel>
                            <S.LabelItem>{`${getTranslateByKey(
                              'information.min'
                            )}:`}</S.LabelItem>
                            <S.LabelResult>
                              {damageSubCategory.minParts}
                            </S.LabelResult>
                          </S.ContainerLabel>
                          <S.ContainerLabel>
                            <S.LabelItem>
                              {`${getTranslateByKey('information.max')}:`}
                            </S.LabelItem>
                            <S.LabelResult>
                              {damageSubCategory.maxParts}
                            </S.LabelResult>
                          </S.ContainerLabel>
                        </TableCell>
                        <TableCell>{damageSubCategory.repairDays}</TableCell>
                        <TableCell width="10%">
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end'
                            }}>
                            <S.TextValue cursor="pointer">
                              <CreateIcon
                                id="id-damageSubCategory-edit"
                                key={`id-damageSubCategory-edit${indexSubCategory}`}
                                onClick={() =>
                                  handleEditDamageSubCategory(
                                    damageCategory,
                                    damageSubCategory
                                  )
                                }
                              />
                            </S.TextValue>
                            <S.TextValue cursor="pointer">
                              {isLoading ? (
                                <LoadingComponent
                                  key={`id-damageSubCategory-load${index}`}
                                  size={10}
                                />
                              ) : (
                                <CloseIcon
                                  id="id-damageSubCategory-delete"
                                  key={`id-damageSubCategory-delete${index}`}
                                  onClick={() =>
                                    handleOpenModalSubCategoryDelete(
                                      damageSubCategory.guidDamageSubCategory
                                    )
                                  }
                                />
                              )}
                            </S.TextValue>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </BoxGrid>
              </S.ContainerGrid>
            )}
        </div>
      ))}

      <DamageSubCategorySidebar
        key={'id-damageSubcategoryModal'}
        open={openModalDamageSubCategory}
        closeModal={closeModalDamageSubCategory}
        damageCategory={damageCategory}
        damageSubCategoryEdit={damageSubCategoryEdit}
        loadGridList={() => {
          setDamageSubCategoryEdit(null);
          refreshData();
        }}
      />

      <KogModalFadeComponent
        open={openModalConfirmDeleteCategory}
        setOpen={setOpenModalConfirmDeleteCategory}>
        <ModalConfirm
          titleModal={getTranslateByKey('information.categoryConfirmDeleted')}
          handleModalCancel={handleCloseModalCategoryDelete}
          handleModalConfirm={handleConfirmModalCategoryDelete}
        />
      </KogModalFadeComponent>

      <KogModalFadeComponent
        open={openModalConfirmDeleteSubCategory}
        setOpen={setOpenModalConfirmDeleteSubCategory}>
        <ModalConfirm
          titleModal={getTranslateByKey(
            'information.subCategoryConfirmDeleted'
          )}
          handleModalCancel={handleCloseModalSubCategoryDelete}
          handleModalConfirm={handleConfirmModalSubCategoryDelete}
        />
      </KogModalFadeComponent>
      <ModalConfirmationReason />
    </>
  );
}

export default DamageCategorySection;
